import variables from 'themes/variables'

import { ITEM_KINDS, SCROLL_END_TOLERANCE_PX } from './constants'

const { sizes } = variables.breakpoints

export const BELOW_DESKTOP_MEDIA_QUERY = `(max-width: ${sizes.medium})`

export const isDesktopViewport = () =>
  typeof window === 'undefined' ||
  !window.matchMedia ||
  !window.matchMedia(BELOW_DESKTOP_MEDIA_QUERY).matches

let panelIdCounter = 0

export const createPanelId = () => {
  panelIdCounter += 1
  return `program-nav-dropdown-${panelIdCounter}`
}

let activeDropdown = null

// Only one program nav menu is open at a time: opening one closes the last.
export const claimActiveDropdown = ({ close, isPointerHeadingInside }) => {
  if (activeDropdown && activeDropdown.close !== close) {
    activeDropdown.close()
  }
  activeDropdown = { close, isPointerHeadingInside }
}

export const releaseActiveDropdown = close => {
  if (activeDropdown?.close === close) {
    activeDropdown = null
  }
}

export const isAnotherDropdownOpen = close =>
  !!activeDropdown && activeDropdown.close !== close

export const isPointerHeadingIntoAnotherDropdown = close =>
  isAnotherDropdownOpen(close) && activeDropdown.isPointerHeadingInside()

export const isPointInRect = ({ point, rect, padding = 0 }) =>
  !!rect &&
  point.x >= rect.left - padding &&
  point.x <= rect.right + padding &&
  point.y >= rect.top - padding &&
  point.y <= rect.bottom + padding

const crossProduct = (point, start, end) =>
  (point.x - end.x) * (start.y - end.y) - (start.x - end.x) * (point.y - end.y)

export const isPointInTriangle = ({ point, corners: [a, b, c] }) => {
  const sides = [
    crossProduct(point, a, b),
    crossProduct(point, b, c),
    crossProduct(point, c, a),
  ]
  const hasNegative = sides.some(side => side < 0)
  const hasPositive = sides.some(side => side > 0)
  return !(hasNegative && hasPositive)
}

/**
 * True when the pointer, moving from `from` to `to` above the panel, is inside
 * the triangle between `from` and the panel's top edge, i.e. aiming at it.
 */
export const isHeadingIntoRect = ({ from, to, rect, padding = 0 }) => {
  if (!from || !to || !rect || to.y >= rect.top) return false
  if (from.x === to.x && from.y === to.y) return false

  return isPointInTriangle({
    point: to,
    corners: [
      from,
      { x: rect.left - padding, y: rect.top },
      { x: rect.right + padding, y: rect.top },
    ],
  })
}

/**
 * Splits dropdown items into columns. A class list or section label starts a
 * new titled column; links before any title share one untitled column.
 * Class list columns hold their own `items` (null while loading).
 */
export const groupItemsIntoColumns = items =>
  (items || []).reduce((columns, item) => {
    if (item.kind === ITEM_KINDS.classList) {
      columns.push({
        kind: ITEM_KINDS.classList,
        title: item.title,
        tier: item.tier,
        items: item.items ?? null,
      })
    } else if (item.kind === ITEM_KINDS.sectionLabel) {
      columns.push({
        kind: ITEM_KINDS.sectionLabel,
        title: item.title,
        items: [],
      })
    } else if (item.kind === ITEM_KINDS.link) {
      const lastColumn = columns[columns.length - 1]
      if (!lastColumn || lastColumn.kind === ITEM_KINDS.classList) {
        columns.push({ kind: ITEM_KINDS.link, items: [item] })
      } else {
        lastColumn.items.push(item)
      }
    }
    return columns
  }, [])

export const hasDropdownContent = items =>
  !!items?.some(
    item => item.kind === ITEM_KINDS.link || item.kind === ITEM_KINDS.classList,
  )

export const isScrolledToEnd = ({ scrollTop, scrollHeight, clientHeight }) =>
  scrollHeight - scrollTop - clientHeight <= SCROLL_END_TOLERANCE_PX

const canScrollInDirection = ({ element, deltaY }) => {
  const { overflowY } = window.getComputedStyle(element)
  if (overflowY !== 'auto' && overflowY !== 'scroll') return false
  if (element.scrollHeight <= element.clientHeight) return false

  return deltaY < 0 ? element.scrollTop > 0 : !isScrolledToEnd(element)
}

/**
 * True when a wheel event over the open menu would scroll the page instead of
 * a column: the menu swallows it so the page does not move under the user.
 * Wheel events that a column under the pointer can still scroll are left
 * alone, as are pinch-zoom gestures (ctrl + wheel).
 */
export const shouldBlockPageScroll = ({ event, boundary }) => {
  if (event.ctrlKey) return false

  let element = event.target
  while (element && element !== boundary) {
    if (canScrollInDirection({ element, deltaY: event.deltaY })) return false
    element = element.parentElement
  }

  return true
}

export const isModifiedClick = event =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey
